/**
 * Seeded pseudo-random number generator using xoshiro256**.
 *
 * **Why xoshiro256\*\*:** small (~30 lines), fast, well-distributed, passes
 * BigCrush. Same algorithm in every modern game-dev library that cares
 * about determinism. Reproducible bit-for-bit across V8 (browser) and
 * Node (validator).
 *
 * **Determinism contract:** pure function of seed + call sequence. No
 * `Math.random`, no `Date.now`, no globals. Two `Xoshiro256ss` with the
 * same seed produce identical sequences forever.
 *
 * Internal state is 4× u64. We use BigInt because JS Numbers can't hold
 * full 64-bit values without precision loss. BigInt arithmetic is slower
 * than Number — if profiling ever shows this as the hot path of a
 * cartridge, we can swap to a u32-pair representation. For arcade-scale
 * runs (60 ticks/s × ~60s), this is fine.
 */
export class Xoshiro256ss {
    /** The seed string used to initialize this PRNG. Echoed for replay attribution. */
    seed;
    s0;
    s1;
    s2;
    s3;
    constructor(seed) {
        if (seed.length === 0) {
            throw new Error('PRNG seed must be a non-empty string');
        }
        this.seed = seed;
        const stream = splitmix64FromString(seed);
        this.s0 = stream();
        this.s1 = stream();
        this.s2 = stream();
        this.s3 = stream();
        if (this.s0 === 0n && this.s1 === 0n && this.s2 === 0n && this.s3 === 0n) {
            this.s0 = 1n;
        }
    }
    /** Float in [0, 1). */
    next() {
        const r = this.nextU64();
        return Number(r >> 11n) / 2 ** 53;
    }
    /** Uniform integer in [min, max] inclusive. */
    int(min, max) {
        if (!Number.isInteger(min) || !Number.isInteger(max)) {
            throw new Error(`PRNG.int requires integer bounds, got (${min}, ${max})`);
        }
        if (max < min) {
            throw new Error(`PRNG.int requires max >= min, got (${min}, ${max})`);
        }
        const range = max - min + 1;
        return min + Math.floor(this.next() * range);
    }
    /** Random element from the array. */
    pick(arr) {
        if (arr.length === 0) {
            throw new Error('PRNG.pick requires a non-empty array');
        }
        return arr[this.int(0, arr.length - 1)];
    }
    /**
     * Branch a child PRNG. The parent advances 4 u64s; the child seed is
     * derived from those, so calling fork() twice in a row produces two
     * different (but reproducible) child PRNGs. Used to give different
     * subsystems independent streams without coupling.
     */
    fork() {
        const childSeed = `${this.seed}/${this.nextU64().toString(16)}-${this.nextU64().toString(16)}`;
        return new Xoshiro256ss(childSeed);
    }
    /** Advance the state and return one u64. */
    nextU64() {
        const result = rotl(this.s1 * 5n, 7n) * 9n;
        const t = this.s1 << 17n;
        this.s2 ^= this.s0;
        this.s3 ^= this.s1;
        this.s1 ^= this.s2;
        this.s0 ^= this.s3;
        this.s2 ^= t;
        this.s3 = rotl(this.s3, 45n);
        return mask64(result);
    }
}
/**
 * Hash a string into a u64 BigInt then run splitmix64. Returns a function
 * that yields successive u64s — used to fill xoshiro256\*\*'s 4×u64 state
 * from a human-readable seed.
 */
function splitmix64FromString(seed) {
    let h = 0xcbf29ce484222325n;
    const FNV_PRIME = 0x100000001b3n;
    for (let i = 0; i < seed.length; i++) {
        h ^= BigInt(seed.charCodeAt(i));
        h = mask64(h * FNV_PRIME);
    }
    let state = h === 0n ? 1n : h;
    return () => {
        state = mask64(state + 0x9e3779b97f4a7c15n);
        let z = state;
        z = mask64((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n);
        z = mask64((z ^ (z >> 27n)) * 0x94d049bb133111ebn);
        return z ^ (z >> 31n);
    };
}
const U64_MASK = 0xffffffffffffffffn;
/** Force a BigInt back into u64 range after arithmetic. */
function mask64(x) {
    return x & U64_MASK;
}
/** Rotate-left within u64. */
function rotl(x, k) {
    return mask64((x << k) | (x >> (64n - k)));
}
/**
 * Convenience factory. Direct `new Xoshiro256ss(seed)` works too; call
 * sites read more cleanly through `createPrng`.
 */
export function createPrng(seed) {
    return new Xoshiro256ss(seed);
}
//# sourceMappingURL=prng.js.map